/**
 * First-touch attribution for the marketing site (build F16).
 *
 * On a visitor's first landing we store the UTM params, `document.referrer`
 * and the landing path in a first-party cookie `pp_attrib` for 30 days. An
 * existing cookie is never overwritten, so the first touch wins. The signup
 * page sends the stored value as `attribution` in its payload.
 */
import { UTM_KEYS } from "./links";

export const ATTRIBUTION_COOKIE = "pp_attrib";
export const ATTRIBUTION_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

const MAX_VALUE_LENGTH = 200;
const MAX_REFERRER_LENGTH = 500;

export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landing_path?: string;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) return trimmed.slice(prefix.length);
  }
  return null;
}

export function readAttribution(): Attribution | null {
  const raw = readCookie(ATTRIBUTION_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    return parsed && typeof parsed === "object" ? (parsed as Attribution) : null;
  } catch {
    return null;
  }
}

/** Builds an attribution record from a URL search string, referrer and path. */
export function buildAttribution(search: string, referrer: string, pathname: string): Attribution {
  const params = new URLSearchParams(search);
  const record: Attribution = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) record[key] = value.slice(0, MAX_VALUE_LENGTH);
  }
  if (referrer) record.referrer = referrer.slice(0, MAX_REFERRER_LENGTH);
  record.landing_path = (pathname || "/").slice(0, MAX_VALUE_LENGTH);
  return record;
}

/** Stores first-touch attribution unless a `pp_attrib` cookie already exists. */
export function captureAttribution(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (readCookie(ATTRIBUTION_COOKIE) !== null) return;
  const record = buildAttribution(window.location.search, document.referrer, window.location.pathname);
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${ATTRIBUTION_COOKIE}=${encodeURIComponent(JSON.stringify(record))}` +
    `; Max-Age=${ATTRIBUTION_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

/**
 * The attribution to send with a signup: the stored first touch, or (when
 * cookies are blocked) one built from the current page.
 */
export function attributionForSignup(): Attribution {
  const stored = readAttribution();
  if (stored) return stored;
  if (typeof window === "undefined") return {};
  return buildAttribution(window.location.search, document.referrer, window.location.pathname);
}
